/**
 * VideoCallModal
 *
 * Full-screen WebRTC video call.  Works in two modes:
 *
 *   Ad-hoc (existing):   room = `conv-{conversationId}`, no booking context.
 *   Scheduled (new):     room = `call-{bookingId}`, booking context provided.
 *
 * The person who opens the modal first joins as 'host'; anyone who opens it
 * while the room is already active joins as 'participant'.
 *
 * Scheduled-mode additions (redesign spec, batch 5, to Meet's tokens):
 *   • Joining lands in the room at once: the self view and the mic and
 *     camera state show while the other side is still to come. The
 *     recording notice is a sheet over the running call, once a session,
 *     worded by the player's age (recordingNotice), with the invite.
 *   • A thin strip on top: the lesson's title, the elapsed time, REC
 *   • Waiting: the other side's real photo, "Justin will be here shortly.",
 *     one line by kind of lesson, and whether your mic and camera are on
 *   • The remote tile at its feed's own aspect, never cropped: a phone's
 *     portrait feed is a tall tile, a laptop's a wide one; the self view
 *     sized by its own feed, borderless, top right, draggable
 *   • A full-width bar of 56 round controls: mic, camera, share screen
 *     (Mac), chat, the coach's Recommend (a star, labelled on the Mac), More
 *     (the invite, the match, Message), and the red Leave
 *   • The call's own chat, for this room only, and Message for the thread
 *   • Leave means leave: this device's media stops and the screen goes at
 *     once; the coach is asked about the lesson after, on the next screen
 *   • No hard close: the five-minute reminder chip, "Scheduled to end at
 *     4:45" / "The room won't close, this is just a reminder"
 *   • Recommend for the coach, the offer sheet for the player, Booked on
 *     both sides once bought
 *
 * Both modes: Screen Share in the bar where the browser can share a screen,
 * shown on the other side the moment its frames arrive.
 */

import { FitText } from './FitText';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  WebRTCProvider,
  FirebaseSignalingAdapter,
  useWebRTC,
  useScreenShare,
  DEFAULT_ICE_SERVERS,
} from '@double-bagel/webrtc-wrapper';
import type { CallConfig } from '@double-bagel/webrtc-wrapper';
import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Microphone,
  MicrophoneSlash,
  VideoCamera,
  VideoCameraSlash,
  PhoneDisconnect,
  UserPlus,
  Record,
  FilmSlate,
  MonitorArrowUp,
  Star,
  CheckCircle,
  DotsThreeVertical,
  User,
  ChatCircle,
  ChatCircleText,
  X,
} from '@phosphor-icons/react';
import ContextMenu from './ContextMenu';
import { useUserIdentity } from '../hooks/useUserIdentity';
import { liveLessonTitle, monthLessonMode } from '../utils/liveLessonText';
import { closeMatchSession, leaveLiveLesson, leaveSettled, readMatchSession, recordJoin, saveLiveChapters } from '../services/liveCallBookings';
import type { MatchSessionState } from '../utils/matchSession';
import { lessonChapters } from '../utils/liveChapters';
import { useToast } from '../hooks/useToast';
import { roomEndChip, type RoomEndChip } from '../utils/roomEndChip';
import { endCallPlan, moreHasInvite } from '../utils/endCall';
import { barButtons, barGroups, type BarButton } from '../utils/callBar';
import { lateJoinLine, lessonKind, noShowInCall, noShowLine } from '../utils/callEnd';
import { isPlayerNoShow, playerNoShowStatus } from '../utils/playerNoShow';
import { NoShowDecision } from './live/NoShowDecision';
import { CallInviteModal } from './CallInviteModal';
import { GUEST_JOIN } from '../config/featureFlags';
import { RoomMatchPlayer } from './live/RoomMatchPlayer';
import { useRoomData } from '../hooks/useRoomData';
import { isMatchMessage } from '../utils/roomMatchSync';
import type { CallInvite, LiveCallBooking } from '../types/liveCall';
import { getInvitesForBooking } from '../services/callInvites';
import { subscribeRecommendations, type LiveRecommendation, type RecommendOffer } from '../services/liveLessons';
import { getActiveMembershipForPlayerAndCoach } from '../services/liveMemberships';
import { RecommendSheet, OfferSheet } from './CallRecommend';
import { OFFER_TITLE, recommendOffers } from '../utils/recommendOffers';
import { isRecommendWire, withWireRecommendations, type RecommendWire } from '../utils/recommendWire';
import { WaitingToAcceptScreen } from './live/RoomScreens';
import { RECORDING_TOAST, RECORDING_TOAST_MS, recordingToastStep, type ToastStep } from '../utils/recordingToast';
import { RoomChatPanel, sendRoomChat, subscribeRoomChat, type RoomChatLine } from './live/RoomChat';
import { joinLiveRoom, subscribeRoomCall } from '../services/liveV2';
import { useAuth } from '../contexts/AuthContext';
import { getUserProfile } from '../services/userProfile';
import { callRoomId, joinGate, roomOpenFor, type RoomJoin } from '../utils/liveRoom';
import { CHAT_SHEET_SHARE, CORNER_INSET, cameraConstraints, cornerPosition, recordingNotice, selfTileSize, stageBox, nearestCorner, selfTopInset, storedSelfCorner, storeSelfCorner, nameLabelPlace, shareLabel, splitParticipants, stageLayout, tracksToRestart, chatWire, isChatWire, mergeChatLines, unreadCount, trackLiveness, type Box, type Corner } from '../utils/liveRoomUi';
import { AV_WIRE_MS, isAvWire, leftAtOf, personState, stateLine, withAvWire, type AvWire, PRESENCE_CHECK_IN_MS } from '../utils/roomPresence';
import { useRoomPresence } from '../hooks/useRoomPresence';
import { CallDiagnostics } from './live/CallDiagnostics';
import { CallPip } from './live/CallPip';
import { useVideoWatchdog } from '../hooks/useVideoWatchdog';
import { useMediaBudget } from '../hooks/useMediaBudget';
import { usePageLoad } from '../hooks/usePageLoad';
import { budgetOn, cameraAsk, isPhone, wantsFarLow } from '../utils/mediaBudget';
import { phoneStage, type Rect } from '../utils/stageLayout';
import { CHROME_HIDE_MS, CONTROL_SELECTOR, chromeTap } from '../utils/callChrome';
import { useCallDiagnostics } from '../hooks/useCallDiagnostics';
import { ROOM_BARS_HINT, useRoomChrome } from '../hooks/useRoomChrome';
import { timeLabel } from '../utils/liveCalendar';
import { useCallRecording } from '../hooks/useCallRecording';
import { probeCors } from '../services/callRecording';
import { useMatchVideoUrl } from '../hooks/useMatchVideoUrl';

// ── Types ─────────────────────────────────────────────────────────────────────

/** How the call was left, for the screen after it. */
/** This side left the call: Leave, for both roles, with no question (decision 54). */
export interface CallLeft {
  how: 'leave';
}

export interface VideoCallModalProps {
  // Ad-hoc mode
  conversationId?: string;
  // Scheduled mode — provide bookingId instead of (or in addition to) conversationId
  bookingId?: string;
  /** ISO timestamp of the start; the room reads it from `booking`. */
  scheduledStartAt?: string;

  participantId: string;
  displayName: string;
  otherParticipantName: string;
  otherParticipantId?: string;
  /** True when the current user is the coach (hides the Coach-didn't-show button). */
  isCoach?: boolean;
  /** Live booking data — enables the shared video panel when playerVideoUrl is set. */
  booking?: LiveCallBooking;
  /** A pair's room outside any lesson (the room page, /room/:roomId). */
  roomId?: string;
  /** A guest's invite token, for a lesson they were invited to. */
  inviteToken?: string;
  onClose: (left: CallLeft) => void;
}

// ── Recording indicator ───────────────────────────────────────────────────────

function RecordingDot() {
  return (
    <span className="flex flex-shrink-0 items-center gap-1 select-none" aria-label="Recording">
      <Record size={9} color="#fff" weight="fill" aria-hidden="true" />
      <span className="text-[12px] leading-[16px] font-medium">REC</span>
    </span>
  );
}

/** A tile's aspect-ratio from its camera's frame (width / height), or the given one before it is known. */
function tileAspect(feed: Box | null, fallback: number): string {
  return String(feed && feed.w && feed.h ? feed.w / feed.h : fallback);
}

/** Whether a media query holds, kept current. */
function useMatch(query: string): boolean {
  const [on, setOn] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const read = () => setOn(m.matches);
    m.addEventListener('change', read);
    return () => m.removeEventListener('change', read);
  }, [query]);
  return on;
}

/**
 * This side's camera and mic at the sensor's own size, which the phone's OS
 * turns as the phone turns (cameraConstraints), else the mic alone, as the
 * wrapper itself falls back.
 */
/**
 * The phone budget (job 2, utils/mediaBudget): whether this device is a phone, and whether the budget is on
 * (a localStorage switch turns it off for measuring). Read as the call opens.
 */
function phoneBudget(): { phone: boolean; on: boolean } {
  // A touch screen, by the media feature or by touch points. The switch's "phone" treats any device as one,
  // for measuring and for tests (an emulated phone loses its touch after a navigation).
  const coarse = (window.matchMedia?.('(pointer: coarse)').matches ?? false) || (navigator.maxTouchPoints ?? 0) > 0;
  let forced = false;
  try { forced = window.localStorage.getItem('db_call_budget') === 'phone'; } catch { /* no storage */ }
  return {
    phone: forced || isPhone({ coarse, shortSide: Math.min(window.screen?.width ?? 0, window.screen?.height ?? 0) || 9999 }),
    on: budgetOn(() => window.localStorage.getItem('db_call_budget')),
  };
}
/** The camera to ask for: the budget's on a phone, else the sensor's landscape size. */
const callCamera = (b: { phone: boolean; on: boolean }): MediaTrackConstraints => (b.phone && b.on ? cameraAsk(true) : cameraConstraints());

async function acquireCallMedia(camera: MediaTrackConstraints): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: true, video: camera });
  } catch {
    return navigator.mediaDevices.getUserMedia({ audio: true, video: false });
  }
}

/** sessionStorage, or null where reading it throws (blocked site data). */
function sessionStore(): Storage | null {
  try { return window.sessionStorage; } catch { return null; }
}

/** A video element's frame size, read when it is known and when it changes. */
function feedSizeOf(el: HTMLVideoElement): Box | null {
  return el.videoWidth && el.videoHeight ? { w: el.videoWidth, h: el.videoHeight } : null;
}

/**
 * The self tile (Meet's floating self-view): a quarter of the stage wide,
 * bottom right to start, dragged anywhere and snapped on release to the
 * nearest of the four corners of the space it sits in (the stage, or the
 * part of it above the phone's chat sheet). A top corner stands `top` down,
 * under what runs along the stage's top (selfTopInset).
 */
function SelfTile({ size, bounds, origin, top, corner, onCorner, children }: {
  /** The stage it sits in, and where the stage stands in the pane. */
  size: Box; bounds: Box; origin: { x: number; y: number }; top: number; corner: Corner; onCorner: (c: Corner) => void;
  children: React.ReactNode;
}) {
  const [dragAt, setDragAt] = useState<{ x: number; y: number } | null>(null);
  const drag = useRef<{ dx: number; dy: number; px: number; py: number } | null>(null);
  const rest = cornerPosition(corner, size, bounds, top);
  const at = dragAt ?? rest;
  return (
    <div
      className={`absolute z-10 cursor-grab touch-none overflow-hidden rounded-[12px] bg-call-tile shadow-[0_2px_12px_rgba(0,0,0,0.4)] active:cursor-grabbing ${dragAt ? '' : 'transition-[left,top] duration-200 ease-out motion-reduce:transition-none'}`}
      style={{ width: size.w, height: size.h, left: origin.x + at.x, top: origin.y + at.y }}
      data-self-view data-corner={corner}
      onPointerDown={(e) => {
        const parent = e.currentTarget.parentElement!.getBoundingClientRect();
        const px = parent.left + origin.x;
        const py = parent.top + origin.y;
        drag.current = { dx: e.clientX - px - at.x, dy: e.clientY - py - at.y, px, py };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        setDragAt({
          x: Math.min(Math.max(0, e.clientX - d.px - d.dx), Math.max(0, bounds.w - size.w)),
          y: Math.min(Math.max(0, e.clientY - d.py - d.dy), Math.max(0, bounds.h - size.h)),
        });
      }}
      onPointerUp={() => {
        drag.current = null;
        if (dragAt) onCorner(nearestCorner(dragAt, size, bounds));
        setDragAt(null);
      }}
      onPointerCancel={() => { drag.current = null; setDragAt(null); }}
    >
      {children}
    </div>
  );
}

// ── Inner call UI ─────────────────────────────────────────────────────────────

function CallUI({
  roomId,
  participantId,
  displayName,
  otherParticipantName,
  otherParticipantId,
  bookingId,
  isCoach,
  booking,
  inviteToken,
  onClose,
}: {
  roomId: string;
  participantId: string;
  displayName: string;
  otherParticipantName: string;
  otherParticipantId?: string;
  bookingId?: string;
  isCoach?: boolean;
  booking?: LiveCallBooking;
  inviteToken?: string;
  onClose: (left: CallLeft) => void;
}) {
  const {
    localStream,
    participants,
    inCall,
    joinRoom,
    audioEnabled,
    videoEnabled,
    toggleAudio,
    toggleVideo,
    setLocalStream,
  } = useWebRTC();
  const { showError } = useToast();

  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const stageVideo = useCallback(() => remoteVideoRef.current, []);
  const [joinError, setJoinError] = useState<string | null>(null);
  // Rule 9: a call outside every lesson waits here until the other side
  // accepts it from the thread.
  const [waitingToAccept, setWaitingToAccept] = useState(false);
  // The recording notice, a toast for 3 seconds (decision 73, utils/recordingToast): booked
  // lessons only, once a browser session (read as the call opens), never after the other side is in.
  const toastSeen = useRef<boolean>((() => {
    try { return sessionStorage.getItem('db_call_notice_seen') === '1'; } catch { return false; }
  })());
  const [toast, setToast] = useState<ToastStep>('waiting');
  const [showInviteModal, setShowInviteModal] = useState(false);
  // The booking's invites, read as the room opens, so Invite Someone shows the lesson's link at once.
  const [invites, setInvites] = useState<CallInvite[] | null>(null);
  useEffect(() => {
    if (!bookingId) return;
    let live = true;
    const coachId = isCoach ? participantId : (otherParticipantId ?? '');
    const playerId = isCoach ? (otherParticipantId ?? '') : participantId;
    getInvitesForBooking(bookingId, coachId, playerId).then((all) => { if (live) setInvites(all); }).catch(() => { /* the sheet reads them itself */ });
    return () => { live = false; };
  }, [bookingId, isCoach, participantId, otherParticipantId]);
  const [showVideoPanel, setShowVideoPanel] = useState(false);
  // Hide Cameras (decision 104): with the match open, the camera tiles go and the match has the whole stage.
  const [camerasHidden, setCamerasHidden] = useState(false);
  // The player's side of the match video: open while the coach has it open,
  // as the coach's player says on the room's data channel.
  const { onData, sendData, onDataChannelOpen } = useRoomData();
  const [budget] = useState(phoneBudget);
  const phoneDevice = budget.phone;
  const budgetIsOn = budget.on;
  // The data channel to the other side is up: chat lines and Recommend go over it from here.
  const [channelOpen, setChannelOpen] = useState(false);
  useEffect(() => onDataChannelOpen(() => setChannelOpen(true)), [onDataChannelOpen]);
  const [playerMatchOpen, setPlayerMatchOpen] = useState(false);

  // The match is session state (decision 130): in the call, each side reads the session's first and opens
  // where it says; the coach's player writes it from then on, and the coach's close says closed.
  const sessionId = booking?.roomSessionId ?? null;
  const [sessionMatch, setSessionMatch] = useState<MatchSessionState | null>(null);
  const sessionRead = useRef<string | null>(null);
  useEffect(() => {
    if (!inCall || !bookingId || !sessionId || sessionRead.current === sessionId) return;
    sessionRead.current = sessionId;
    readMatchSession(bookingId, sessionId).then((m) => {
      if (!m?.open || !booking?.playerVideoUrl) return;
      setSessionMatch(m);
      if (isCoach) setShowVideoPanel(true); else setPlayerMatchOpen(true);
    }).catch(() => { /* not readable: the data channel still brings the coach's state */ });
  }, [inCall, bookingId, sessionId, isCoach, booking?.playerVideoUrl]);
  const matchSession = useMemo(
    () => (bookingId && sessionId ? { bookingId, sessionId, by: participantId } : undefined),
    [bookingId, sessionId, participantId],
  );
  const setCoachMatch = (open: boolean) => {
    setShowVideoPanel(open);
    if (!open && matchSession) closeMatchSession(matchSession.bookingId, matchSession.sessionId, participantId).catch(() => {});
  };
  useEffect(() => onData((_from, data) => {
    if (isCoach || !isMatchMessage(data)) return;
    // The coach's opening, or any heartbeat after it: a missed "open" costs at most 100 ms.
    if (data.kind === 'open' || data.kind === 'state') setPlayerMatchOpen(true);
    if (data.kind === 'close') setPlayerMatchOpen(false);
  }), [onData, isCoach]);
  useEffect(() => onData((_from, data) => {
    if (!isCoach && isRecommendWire(data)) setRecWire((w) => [...w, data]);
  }), [onData, isCoach]);
  const [showRecommend, setShowRecommend] = useState(false);
  const [moreMenu, setMoreMenu] = useState<{ x: number; y: number } | null>(null);
  const otherIdentity = useUserIdentity(otherParticipantId, isCoach ? 'player' : 'coach');
  const otherFirst = otherParticipantName.split(' ')[0] || otherParticipantName;
  const large = useMatch('(min-width: 768px)');
  // A phone turned sideways: the room full screen through the Fullscreen API,
  // as the editor does, and the strip gone. Turning is not the gesture the API
  // needs, so when the request is refused the next tap asks again. iPhone
  // Safari has no element full screen; there the strip still goes.
  const immersive = useMatch('(pointer: coarse) and (orientation: landscape)');
  // iPhone Safari has no element full screen: Safari's bars as the editor
  // does them, a swipe up to collapse them and the room fitted above them.
  const chrome = useRoomChrome(immersive);
  const roomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = roomRef.current;
    if (!el || typeof el.requestFullscreen !== 'function' || !document.fullscreenEnabled) return;
    if (!immersive) {
      if (document.fullscreenElement === el) void document.exitFullscreen().catch(() => {});
      return;
    }
    if (document.fullscreenElement) return;
    // The next touch asks again: touchend and click carry the user activation
    // WebKit needs (a touch's pointerdown does not).
    let armed = false;
    const disarm = () => { window.removeEventListener('touchend', onTap, true); window.removeEventListener('click', onTap, true); };
    const onTap = () => {
      disarm();
      el.requestFullscreen().catch(() => { /* refused again: the room stays above Safari's bars (useRoomChrome) */ });
    };
    el.requestFullscreen().catch(() => {
      armed = true;
      window.addEventListener('touchend', onTap, true);
      window.addEventListener('click', onTap, true);
    });
    return () => { if (armed) disarm(); };
  }, [immersive]);
  const { userProfile, currentUser } = useAuth();
  // The player's age on the profile, for the notice's words and the invite's name.
  const ownAge = userProfile?.playerProfile?.age ?? null;
  const [otherAge, setOtherAge] = useState<number | null>(null);
  const playerId = booking?.playerId;
  useEffect(() => {
    if (!isCoach || !playerId) return;
    let live = true;
    getUserProfile(playerId).then((p) => { if (live) setOtherAge(p?.playerProfile?.age ?? null); }).catch(() => { /* adult wording */ });
    return () => { live = false; };
  }, [isCoach, playerId]);
  const notice = recordingNotice(isCoach ? otherAge : ownAge);
  // The bar's clock (163) shows once this side has joined: the server's stamp of the join
  // (joinLiveLesson), else the scheduled start.
  const joinedAt = booking ? (isCoach ? booking.coachJoinedAt : booking.playerJoinedAt) ?? booking.scheduledStartAt : null;
  // A second's clock: the elapsed time, and presence going stale.
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const [recommendations, setRecommendations] = useState<LiveRecommendation[]>([]);
  // The coach's Recommend as it went over the room's data channel (recommendWire).
  const [recWire, setRecWire] = useState<RecommendWire[]>([]);
  // Offers closed with the X stay closed for the browser session, a Rejoin included.
  const dismissedKey = `db_call_offers_closed_${bookingId ?? roomId}`;
  const [dismissedOffers, setDismissedOffers] = useState<RecommendOffer[]>(() => {
    try { return JSON.parse(sessionStorage.getItem(dismissedKey) ?? '[]') as RecommendOffer[]; } catch { return []; }
  });
  const closeOffer = useCallback((offer: RecommendOffer) => {
    setDismissedOffers((d) => {
      const next = d.includes(offer) ? d : [...d, offer];
      try { sessionStorage.setItem(dismissedKey, JSON.stringify(next)); } catch { /* this call only */ }
      return next;
    });
  }, [dismissedKey]);
  const [heldOffer, setHeldOffer] = useState<RecommendOffer | null>(null);
  const [viewingOffer, setViewingOffer] = useState<RecommendOffer | null>(null);
  const [chipsClosed, setChipsClosed] = useState<{ reminder: string | null; booked: boolean }>({ reminder: null, booked: false });
  // Whether the player already has Monthly Coaching with this coach: then it is not offered again.
  const [playerIsMember, setPlayerIsMember] = useState(false);
  const pairPlayer = booking?.playerId;
  const pairCoach = booking?.coachId;
  useEffect(() => {
    if (!pairPlayer || !pairCoach) return;
    let live = true;
    getActiveMembershipForPlayerAndCoach(pairPlayer, pairCoach)
      .then((m) => { if (live) setPlayerIsMember(!!m); })
      .catch(() => { /* offered as the lesson allows; the server refuses a second one */ });
    return () => { live = false; };
  }, [pairPlayer, pairCoach]);
  // What the coach may recommend in this lesson; nothing in a Monthly Coaching lesson, so no button.
  const offers = booking ? recommendOffers(booking, { member: playerIsMember }) : [];
  const { startScreenShare, stopSharing, sharedSources } = useScreenShare();
  // Sharing a screen, as distinct from the coach's match, which goes out the
  // same way (a shared source of its own) and is not "sharing your screen".
  const screenSources = sharedSources.filter((x) => x.kind === 'screen');
  const isSharing = screenSources.length > 0;
  const stopScreenShare = useCallback(() => {
    for (const x of screenSources) void stopSharing(x.id).catch(() => { /* already stopped */ });
  }, [screenSources, stopSharing]);
  // A phone browser cannot share its screen, so the control is not offered.
  const canShareScreen = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

  // Recommendations in this call, on the caller's side of the read rule.
  const hasBooking = !!booking;
  useEffect(() => {
    if (!bookingId || !hasBooking) return;
    return subscribeRecommendations(bookingId, participantId, isCoach ? 'coach' : 'player', setRecommendations);
  }, [bookingId, hasBooking, participantId, isCoach]);

  // The player's side: the newest offer still to answer is a banner at the
  // top, "Justin recommends Monthly Coaching" with View and a close X; View
  // opens its sheet, held open after paying so it can say Booked.
  // With what came over the room's channel at the coach's tap: the banner at once, the server's record when it lands.
  const shownRecs = isCoach ? recommendations : withWireRecommendations(recommendations, recWire, clock, (w): LiveRecommendation => ({
    offer: w.offer, title: OFFER_TITLE[w.offer], note: w.note, coachId: otherParticipantId ?? '', playerId: participantId,
    offeringId: null, priceCents: w.priceCents, copyLines: [], status: 'sent', sentAt: w.sentAt,
  }));
  const bannerOffer = isCoach ? null : [...shownRecs]
    .filter((r) => r.status === 'sent' && !dismissedOffers.includes(r.offer) && !(playerIsMember && r.offer === 'membership'))
    .sort((a, b) => b.sentAt.localeCompare(a.sentAt))[0] ?? null;
  const openOffer = isCoach ? null : shownRecs.find((r) => r.offer === (heldOffer ?? viewingOffer)) ?? null;
  const bookedOffer = recommendations.find((r) => r.status === 'booked');

  const hasVideo = !!booking?.playerVideoUrl;

  // Booked calls: check in every 15 seconds while in the room, with this
  // side's camera, mic and share, so the server's presence for this side
  // (joins/{uid}) is what the other side reads; at once when one of them
  // changes. The leave is recorded when the screen goes.
  const media = { cameraOn: videoEnabled, micOn: audioEnabled, sharing: isSharing };
  const mediaRef = useRef(media);
  mediaRef.current = media;
  useEffect(() => {
    if (!bookingId) return;
    const id = setInterval(() => {
      recordJoin(bookingId, isCoach ? 'coach' : 'player', mediaRef.current).catch(() => { /* best-effort */ });
    }, PRESENCE_CHECK_IN_MS);
    return () => {
      clearInterval(id);
      leaveLiveLesson(bookingId).catch(() => { /* best-effort */ });
    };
  }, [bookingId, isCoach]);
  useEffect(() => {
    if (!bookingId || !inCall) return;
    recordJoin(bookingId, isCoach ? 'coach' : 'player', mediaRef.current).catch(() => { /* best-effort */ });
  }, [bookingId, inCall, isCoach, videoEnabled, audioEnabled, isSharing]);

  // Presence for the banner on the other side: a heartbeat on this side's
  // entry every 10 seconds while in the call, well inside the 35 seconds
  // after which the other side reads a dropped connection (the adapter's `active` flag
  // alone outlives a closed tab).
  useEffect(() => {
    if (!inCall) return;
    const ref = doc(db, 'webrtc_rooms', roomId, 'participants', participantId);
    const beat = () => { updateDoc(ref, { lastSeenAt: serverTimestamp() }).catch(() => { /* best-effort */ }); };
    beat();
    const id = setInterval(beat, 10_000);
    // A closed tab or app leaves too: left active, this entry is a dead
    // connection the other side keeps offering to, and this side's next join
    // under the same id is taken for it.
    const gone = () => { updateDoc(ref, { active: false }).catch(() => { /* best-effort */ }); };
    window.addEventListener('pagehide', gone);
    return () => { clearInterval(id); window.removeEventListener('pagehide', gone); };
  }, [inCall, roomId, participantId]);

  // This side's share, as presence for the other side: a screen. The match
  // is not a share; it goes over the room's data channel (RoomMatchPlayer).
  const sharingNow = isSharing;
  useEffect(() => {
    if (!inCall) return;
    updateDoc(doc(db, 'webrtc_rooms', roomId, 'participants', participantId), { sharing: sharingNow }).catch(() => { /* best-effort */ });
  }, [inCall, sharingNow, roomId, participantId]);

  // Away from the tab: iOS stops the camera while Safari is in the
  // background. The other side sees Camera paused (paused on this side's
  // entry), not a frozen frame. Back on the tab, the tracks iOS stopped start
  // again at once and go out on the same senders (setLocalStream replaces
  // the tracks, no renegotiation), with the mic and camera as they were;
  // audio that kept running is left alone.
  const mediaNow = useRef({ localStream, audioEnabled, videoEnabled });
  mediaNow.current = { localStream, audioEnabled, videoEnabled };
  useEffect(() => {
    if (!inCall) return;
    const entry = doc(db, 'webrtc_rooms', roomId, 'participants', participantId);
    let restarting = false;
    const onVisibility = async () => {
      if (document.visibilityState === 'hidden') {
        updateDoc(entry, { paused: true }).catch(() => { /* best-effort */ });
        return;
      }
      const { localStream: stream, audioEnabled: micOn, videoEnabled: camOn } = mediaNow.current;
      if (stream && !restarting) {
        const redo = tracksToRestart(stream.getTracks());
        if (redo.video || redo.audio) {
          restarting = true;
          try {
            const fresh = await navigator.mediaDevices.getUserMedia({
              video: redo.video ? callCamera(budget) : false, audio: redo.audio,
            });
            const keep = stream.getTracks().filter((t) => (t.kind === 'video' ? !redo.video : !redo.audio));
            const next = new MediaStream([...keep, ...fresh.getTracks()]);
            next.getVideoTracks().forEach((t) => { t.enabled = camOn; });
            next.getAudioTracks().forEach((t) => { t.enabled = micOn; });
            await setLocalStream(next);
            stream.getTracks().filter((t) => !keep.includes(t)).forEach((t) => t.stop());
          } catch (err) {
            console.error('[VideoCall] could not restart the camera:', err);
          } finally {
            restarting = false;
          }
        }
      }
      updateDoc(entry, { paused: false }).catch(() => { /* best-effort */ });
    };
    const handler = () => { void onVisibility(); };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [inCall, roomId, participantId, setLocalStream, budget]);

  // Back on the tab (job 4): the call's own videos, the stage, the column's tiles and this side's view, play
  // again wherever the browser paused them while hidden. The floating window shows clones (CallPip), so it
  // never holds these elements' tracks. The match is the match player's own to resume.
  useEffect(() => {
    if (!inCall) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      roomRef.current?.querySelectorAll<HTMLVideoElement>('[data-remote-tile] video, [data-camera-tile] video, [data-self-view] video, [data-grid-tile] video')
        .forEach((v) => { if (v.paused && v.srcObject) void v.play().catch(() => { /* the watchdog tries again */ }); });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [inCall]);

  // The end reminder: 5 minutes before the scheduled end until the end, in
  // the viewer's zone. The room does not close then.
  const [endChip, setEndChip] = useState<RoomEndChip | null>(null);
  const [lastChip, setLastChip] = useState<RoomEndChip | null>(null);
  useEffect(() => {
    if (!booking) return;
    // The viewer's zone as stored on the lesson, else the device's.
    const zone = (isCoach ? booking.coachTimeZone : booking.playerTimeZone) ||
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
    const tick = () => {
      const chip = roomEndChip(booking.scheduledStartAt, booking.durationMinutes, Date.now(), zone);
      setEndChip(chip);
      if (chip) setLastChip(chip);
    };
    tick();
    const id = setInterval(tick, 15_000);
    return () => clearInterval(id);
  }, [booking, isCoach]);


  // Join the room at once; nothing gates it.
  useEffect(() => {
    let cancelled = false;
    let stopWaiting: (() => void) | null = null;
    const join = async () => {
      try {
        // Rule 9: a pair's room joins through the server first. In a lesson's
        // window it is that lesson (recorded there); outside every window it
        // is a call that connects once both accept; a guest needs an invite.
        // Signalling on the room is open only after this (firestore.rules).
        let serverJoined = false;
        const roomRef = doc(db, 'webrtc_rooms', roomId);
        // The camera and the room's state are fetched while the server join
        // runs, not after it: the handshake starts as soon as the room opens.
        let mediaAsk: Promise<MediaStream> | null = acquireCallMedia(callCamera(budget));
        const roomAsk = getDoc(roomRef);
        mediaAsk.catch(() => { /* raised where it is awaited */ });
        roomAsk.catch(() => { /* raised where it is awaited */ });
        // The server join when the room was already open: it runs beside the handshake.
        let serverBeside: Promise<RoomJoin> | null = null;
        let serverAsk: Promise<RoomJoin> | null = null;
        if (roomId.startsWith('room_')) {
          // The server join goes after a leave still landing (a Rejoin straight after Leave), never before it.
          serverAsk = leaveSettled().then(() => joinLiveRoom({ roomId, ...(inviteToken && { token: inviteToken }) }));
          // Already open for this person (the other side's join opened it, or this is a rejoin):
          // signalling is allowed now, so the handshake starts at once and the server join, which
          // records the lesson, runs beside it. On staging a cold joinLiveRoom held both sides
          // for 5 s before any connection (QA's walk of 29 October).
          const open = await getDoc(doc(db, 'liveRooms', roomId))
            .then((d) => roomOpenFor(d.data(), participantId, Date.now()), () => false);
          if (cancelled) { void mediaAsk.then((m) => m.getTracks().forEach((t) => t.stop()), () => {}); return; }
          if (open) {
            serverBeside = serverAsk;
            serverAsk.catch(() => { /* recorded below instead */ });
          }
        }
        if (serverAsk && !serverBeside) {
          const r = await serverAsk;
          if (cancelled) { void mediaAsk.then((m) => m.getTracks().forEach((t) => t.stop()), () => {}); return; }
          serverJoined = r.kind === 'lesson';
          if (joinGate(r) === 'wait') {
            // No camera while waiting for the other side to accept.
            void mediaAsk.then((m) => m.getTracks().forEach((t) => t.stop()), () => {});
            mediaAsk = null;
            setWaitingToAccept(true);
            await new Promise<void>((resolve) => {
              stopWaiting = subscribeRoomCall(roomId, (c) => {
                if (c?.status === 'connected') resolve();
              });
            });
            stopWaiting?.();
            stopWaiting = null;
            if (cancelled) return;
            setWaitingToAccept(false);
          }
        }
        const snap = mediaAsk ? await roomAsk : await getDoc(roomRef);
        const roomExists = snap.exists() && snap.data()?.status === 'active';
        if (cancelled) { void mediaAsk?.then((m) => m.getTracks().forEach((t) => t.stop()), () => {}); return; }
        const role = roomExists ? 'participant' : 'host';
        const media = await (mediaAsk ?? acquireCallMedia(callCamera(budget)));
        if (cancelled) { media.getTracks().forEach((t) => t.stop()); return; }
        await joinRoom(roomId, displayName, role, media);
        if (cancelled) return;
        if (role === 'host') {
          const allUids = otherParticipantId
            ? [participantId, otherParticipantId]
            : [participantId];
          // setDoc with merge so this succeeds even when FirebaseSignalingAdapter's
          // own updateDoc silently failed on a brand-new room (doc didn't exist yet).
          // A new call starts with an empty chat: the chat is this call's only.
          await setDoc(roomRef, {
            initiatorUid: participantId,
            initiatorName: displayName,
            allParticipantUids: allUids,
            status: 'active',
            conversationId: roomId.startsWith('conv-') ? roomId.replace(/^conv-/, '') : null,
            bookingId: bookingId ?? null,
            // This call's session, for its chat (a lesson's is its booking).
            callSessionId: bookingId ?? `${participantId}-${Date.now()}`,
            chat: [],
          }, { merge: true });
        }
        if (serverBeside) serverJoined = await serverBeside.then((r) => r.kind === 'lesson', () => false);
        if (cancelled) return;
        // Record join time for payout guard (the server did, for a pair's room)
        if (bookingId && !serverJoined) {
          await recordJoin(bookingId, isCoach ? 'coach' : 'player').catch(() => { /* best-effort */ });
        }
      } catch (err) {
        if (!cancelled) {
          setJoinError('Could not connect. Check your camera and microphone permissions.');
          console.error('[VideoCall] join failed:', err);
        }
      }
    };
    void join();
    return () => { cancelled = true; stopWaiting?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // The self view's feed, for its size: the element's frame as it is drawn
  // (rotation applied), else the track's own settings, read again on every
  // resize; never a width and height kept from before.
  const [localFeed, setLocalFeed] = useState<Box | null>(null);
  const trackSize = useCallback((): Box | null => {
    const st = localStream?.getVideoTracks()[0]?.getSettings();
    return st?.width && st?.height ? { w: st.width, h: st.height } : null;
  }, [localStream]);
  const localVideoCallback = useCallback((el: HTMLVideoElement | null) => {
    if (!el) return;
    el.srcObject = localStream ?? null;
    const read = () => setLocalFeed(feedSizeOf(el) ?? trackSize());
    el.onloadedmetadata = read;
    el.onresize = read;
    read();
  }, [localStream, trackSize]);


  // The person this lesson is with, by id (a guest may connect first), and
  // anyone else in the room: with someone else there, three tiles.
  const { main: remoteParticipant, others: otherPeople } = splitParticipants(participants, otherParticipantId);
  // Identify streams by track composition:
  //   camera  = has audio + has video (microphone + camera)
  //   canvas  = no audio + has video  (screen/canvas broadcast)
  //   vidAudio = has audio + no video  (video playback audio from coach's player)
  const remoteCameraStream = remoteParticipant?.streams.find(
    (s) => s.getAudioTracks().length > 0 && s.getVideoTracks().length > 0,
  ) ?? null;
  const remoteCanvasStream = remoteParticipant?.streams.find(
    (s) => s.getAudioTracks().length === 0 && s.getVideoTracks().length > 0,
  ) ?? null;
  const remoteVideoAudioStream = remoteParticipant?.streams.find(
    (s) => s.getAudioTracks().length > 0 && s.getVideoTracks().length === 0,
  ) ?? null;

  // Whether the shared screen is showing frames. A track just added arrives
  // muted and unmutes once media flows, so the share appears the moment it
  // does, not on some later renegotiation; ended and inactive end it.
  const [canvasStreamLive, setCanvasStreamLive] = useState(false);
  useEffect(() => {
    if (!remoteCanvasStream) { setCanvasStreamLive(false); return; }
    const tracks = remoteCanvasStream.getVideoTracks();
    const start = setTimeout(() => setCanvasStreamLive((l) => trackLiveness(l, tracks.some((t) => t.muted) ? 'mute' : 'start')), 0);
    const onMute = () => setCanvasStreamLive((l) => trackLiveness(l, 'mute'));
    const onUnmute = () => setCanvasStreamLive((l) => trackLiveness(l, 'unmute'));
    const onEnded = () => setCanvasStreamLive((l) => trackLiveness(l, 'ended'));
    const onInactive = () => setCanvasStreamLive((l) => trackLiveness(l, 'inactive'));
    tracks.forEach((t) => {
      t.addEventListener('mute', onMute); t.addEventListener('unmute', onUnmute); t.addEventListener('ended', onEnded);
    });
    remoteCanvasStream.addEventListener('inactive', onInactive);
    return () => {
      clearTimeout(start);
      tracks.forEach((t) => {
        t.removeEventListener('mute', onMute); t.removeEventListener('unmute', onUnmute); t.removeEventListener('ended', onEnded);
      });
      remoteCanvasStream.removeEventListener('inactive', onInactive);
    };
  }, [remoteCanvasStream]);

  // Their camera's track sending no frames for a second (muted): the stage
  // does not hold its last frame. Unmuted, the feed is back at once.
  const [remoteCameraStalled, setRemoteCameraStalled] = useState(false);
  useEffect(() => {
    const track = remoteCameraStream?.getVideoTracks()[0];
    if (!track) { setRemoteCameraStalled(false); return; }
    let timer: ReturnType<typeof setTimeout> | null = null;
    // A track just added arrives muted until media flows: only a feed that has shown frames can stall.
    let hadFrames = false;
    const onMute = () => { if (hadFrames && !timer) timer = setTimeout(() => setRemoteCameraStalled(true), 1000); };
    const onUnmute = () => { hadFrames = true; if (timer) { clearTimeout(timer); timer = null; } setRemoteCameraStalled(false); };
    track.addEventListener('mute', onMute);
    track.addEventListener('unmute', onUnmute);
    track.addEventListener('ended', onMute);
    if (!track.muted) onUnmute(); else setRemoteCameraStalled(false);
    return () => {
      if (timer) clearTimeout(timer);
      track.removeEventListener('mute', onMute);
      track.removeEventListener('unmute', onUnmute);
      track.removeEventListener('ended', onMute);
    };
  }, [remoteCameraStream]);

  // The other person's state, from their presence and never from tracks:
  // not joined yet, in with the camera on or off, or left (roomPresence).
  const presenceRead = useRoomPresence({
    bookingId, roomId, uid: otherParticipantId, side: isCoach ? 'player' : 'coach', booking,
  });
  // Their camera and mic as they tapped them, over the channel, ahead of presence (job 8).
  const [avWire, setAvWire] = useState<{ cam?: boolean; mic?: boolean; at: number } | null>(null);
  useEffect(() => onData((_from, data) => {
    if (isAvWire(data)) setAvWire((w) => ({ ...(w && Date.now() - w.at < AV_WIRE_MS ? w : {}), ...(data.cam !== undefined ? { cam: data.cam } : {}), ...(data.mic !== undefined ? { mic: data.mic } : {}), at: Date.now() }));
  }), [onData]);
  const theirPresence = withAvWire(presenceRead, avWire, Date.now());
  // Quiet with no Leave is a dropped connection, once their feed has stopped
  // too; a missed check-in over a feed still playing is not a drop.
  const presenceState = personState(theirPresence, clock);
  const feedFlowing = !!remoteCameraStream && !remoteCameraStalled
    && remoteCameraStream.getVideoTracks().some((t) => t.readyState === 'live' && !t.muted);
  const theirState = presenceState === 'dropped' && feedFlowing
    ? (theirPresence?.cameraOn === false ? 'camera_off' : 'camera_on')
    : presenceState;
  const theyAreIn = theirState === 'camera_on' || theirState === 'camera_off' || theirState === 'camera_paused';
  const presenceKnown = !!theirPresence;
  useEffect(() => {
    if (toast !== 'shown') return;
    try { sessionStorage.setItem('db_call_notice_seen', '1'); } catch { /* this call only */ }
    const id = setTimeout(() => setToast('gone'), RECORDING_TOAST_MS);
    return () => clearTimeout(id);
  }, [toast]);
  const remoteMuted = theirPresence?.micOn === false;
  const remoteCameraOff = theirState === 'camera_off';
  const viewerZone = (isCoach ? booking?.coachTimeZone : booking?.playerTimeZone) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  const leftIso = theirState === 'left' ? leftAtOf(theirPresence) : null;

  // The media: their share while they say they are sharing, else their
  // camera's stream, which also carries their voice when the camera is off.
  const effectiveCanvasStream = canvasStreamLive && theirPresence?.sharing ? remoteCanvasStream : null;
  const mainDisplayStream = effectiveCanvasStream ?? (theyAreIn ? remoteCameraStream : null);
  // What the stage shows over the media: nothing over a feed or a share; their photo otherwise, with their state's line.
  // A feed that stops sending frames while they say the camera is on (their
  // phone stopped it before it could say so) is Camera paused too, never a
  // frozen frame; their voice keeps playing from the same element.
  const stalled = remoteCameraStalled && theirState === 'camera_on';
  const shownState = stalled ? 'camera_paused' : theirState;
  const onCamera = !!effectiveCanvasStream || (shownState === 'camera_on' && !!remoteCameraStream);
  // Ten minutes after the start with the other person not there, the clock has decided (57, 58): its line.
  const noShow = shownState === 'not_joined' && bookingId && booking
    ? noShowLine({ isCoach: !!isCoach, otherFirst, kind: lessonKind(booking), startIso: booking.scheduledStartAt, nowMs: clock }) : null;
  // In late, after the 10-minute mark, with the coach gone: the no-show stands (78).
  const lateLine = booking ? lateJoinLine({ isCoach: !!isCoach, ownJoinedAt: booking.playerJoinedAt, startIso: booking.scheduledStartAt, theirState: shownState, theirLeftAt: leftAtOf(theirPresence), otherFirst }) : null;
  // A player no-show the server holds (154, booking-contracts.md 13): its line for either side while the other
  // is not in, and the coach's Count This Lesson and Offer a New Time once it is pending.
  const held = booking && bookingId && isPlayerNoShow(booking) ? playerNoShowStatus(booking, !!isCoach, viewerZone) : null;
  const theyAreOn = shownState === 'camera_on' || shownState === 'camera_off' || shownState === 'camera_paused';
  const noShowNow = noShowInCall({ isCoach: !!isCoach, theyAreIn: theyAreOn, clockLine: noShow, held });
  const stageLine = joinError ?? (effectiveCanvasStream ? null : noShowNow?.line ?? lateLine ?? stateLine(shownState, otherFirst, leftIso ? timeLabel(leftIso, viewerZone) : null));
  // The match video open on either side, or a shared screen, puts the stage in
  // the middle and the two cameras in the rail: a right column on a wider
  // screen, a bottom row on a phone.
  const matchOpen = !!booking?.playerVideoUrl && !!bookingId && (isCoach ? showVideoPanel : playerMatchOpen && inCall);
  // A share takes the stage beside the match (144): the match moves to the top of the side column, never
  // closing; this side's own share shows "You're sharing your screen" on its stage (stageLayout).
  // On a phone with the match open and no share (decision 133): the tiles a column on the stage's right,
  // each at its camera's own shape, so a portrait camera is a vertical tile; the match has the rest.
  // The match's full screen in the call (162): the call's own stage, never the system player. The match
  // takes the whole stage, the cameras out of the way; where the browser has the Fullscreen API, the room
  // fills the screen too.
  const [stageFull, setStageFull] = useState(false);
  const stageFullRef = useRef(false);
  // Whether this full screen asked the browser for its own: then leaving it leaves the browser's too.
  const tookBrowserFull = useRef(false);
  const toggleStageFull = useCallback(() => {
    const el = roomRef.current;
    const on = stageFullRef.current;
    if (!on) {
      if (el && typeof el.requestFullscreen === 'function' && document.fullscreenEnabled && !document.fullscreenElement) {
        tookBrowserFull.current = true;
        void el.requestFullscreen().then(() => {
          // Turned off before the browser answered: there was nothing to leave then, so leave it now.
          if (stageFullRef.current || !tookBrowserFull.current || !document.fullscreenElement) return;
          tookBrowserFull.current = false;
          void document.exitFullscreen().catch(() => {});
        }).catch(() => { tookBrowserFull.current = false; /* the stage alone is the full screen here */ });
      }
    } else if (tookBrowserFull.current && document.fullscreenElement) {
      tookBrowserFull.current = false;
      void document.exitFullscreen().catch(() => {});
    }
    stageFullRef.current = !on;
    setStageFull(!on);
  }, []);
  useEffect(() => {
    const onChange = () => {
      if (document.fullscreenElement || !tookBrowserFull.current) return;
      tookBrowserFull.current = false;
      stageFullRef.current = false;
      setStageFull(false);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  useEffect(() => {
    if (matchOpen || !stageFullRef.current) return;
    stageFullRef.current = false;
    if (tookBrowserFull.current && document.fullscreenElement) { tookBrowserFull.current = false; void document.exitFullscreen().catch(() => {}); }
    setStageFull(false);
  }, [matchOpen]);
  const camerasHiddenNow = camerasHidden || stageFull;
  const layout = stageLayout({ inCall, matchOpen, theirShare: !!effectiveCanvasStream, ownShare: isSharing, large, camerasHidden: camerasHiddenNow });
  // The call's recording, on by default in every call (docs/call-recording.md): this side's tracks
  // go up as it records, and the compile job lays them out when everyone has left.
  const recording = useCallRecording({
    roomId, uid: currentUser?.uid ?? null, inCall, localStream: localStream ?? null,
    remoteStreams: participants.filter((x) => !x.isLocal).flatMap((x) => x.streams),
    shareStream: screenSources[0]?.stream ?? null, matchOpen, phone: !large,
  });
  const recordingOn = recording.enabled;
  // The notice, once a session, in any call while it is recorded.
  useEffect(() => {
    const next = recordingToastStep({ step: toast, recorded: recordingOn, seenThisSession: toastSeen.current, joined: inCall, presenceKnown, theyAreIn });
    if (next !== toast) setToast(next);
  }, [toast, recordingOn, inCall, presenceKnown, theyAreIn]);
  // The player's match loads with CORS where it can, so the recording can draw it: asked now, before it opens.
  // The match plays from a URL that lasts (a stored 7 day link is asked for again on read; 3 Nov).
  const matchUrl = useMatchVideoUrl(booking && bookingId ? { ...booking, id: bookingId } : null);
  useEffect(() => { if (!isCoach && matchUrl) void probeCors(matchUrl); }, [isCoach, matchUrl]);
  const { sidebar: sidebarActive, phoneMatchRail, camerasOff, matchInColumn } = layout;
  // The phone budget (job 2): this camera held to it, and the other side's sent low while a phone has the
  // match or a share on its stage; their ask applied here in turn.
  const { farWantsLow } = useMediaBudget({
    on: budgetIsOn, phone: phoneDevice, localStream: localStream ?? null, sendData, onData,
    wantFarLow: wantsFarLow({ phone: phoneDevice, budgetOn: budgetIsOn, matchOpen, shareOnStage: layout.shareOnStage }),
  });
  const threeTiles = inCall && otherPeople.length > 0 && !sidebarActive;

  // Sidebar camera tiles use callback refs so srcObject is set on mount AND on stream changes.
  // When the dep stream changes, React swaps the callback identity, which detaches (null) then
  // reattaches (el) the ref — setting the new srcObject without a separate useEffect.
  const videoAudioCallback = useCallback((el: HTMLAudioElement | null) => {
    if (el) el.srcObject = remoteVideoAudioStream;
  }, [remoteVideoAudioStream]);

  const tileVideoRef = useRef<HTMLVideoElement | null>(null);
  const tileVideo = useCallback(() => tileVideoRef.current, []);
  // Their camera's shape, read off its tile, for the phone's layout (decision 162).
  const [themAspect, setThemAspect] = useState(16 / 9);
  const sidebarRemoteVideoCallback = useCallback((el: HTMLVideoElement | null) => {
    tileVideoRef.current = el;
    if (!el) return;
    el.srcObject = remoteCameraStream;
    const read = () => { if (el.videoWidth && el.videoHeight) setThemAspect(el.videoWidth / el.videoHeight); };
    el.onloadedmetadata = read;
    el.onresize = read;
    read();
  }, [remoteCameraStream]);
  // Their camera off then on is the same track enabled again; the elements showing it are kept showing
  // it (useVideoWatchdog): the stage while it holds their camera or share, the column's tile while the
  // match or a share has the stage. How often each had to step in shows in the diagnostics (job 1, 3 Nov).
  const stageRestarts = useVideoWatchdog(stageVideo, mainDisplayStream,
    !!mainDisplayStream && (!!effectiveCanvasStream || theirState === 'camera_on'));
  const tileRestarts = useVideoWatchdog(tileVideo, remoteCameraStream,
    sidebarActive && !camerasOff && !!remoteCameraStream && theirState === 'camera_on');
  const sidebarLocalVideoCallback = useCallback((el: HTMLVideoElement | null) => {
    if (el) el.srcObject = localStream ?? null;
  }, [localStream]);

  // The remote feed's frame size, read from the element as the frames arrive, on each
  // loadedmetadata and resize (an iPhone turned resizes its feed), and once as it
  // attaches. The stage letterboxes the feed (object-contain); the size is on the
  // stage as data-feed, for walks and tests.
  const [remoteFeed, setRemoteFeed] = useState<Box | null>(null);
  useEffect(() => {
    const el = remoteVideoRef.current;
    if (!el) return;
    el.srcObject = mainDisplayStream;
    setRemoteFeed(null);
    if (!mainDisplayStream) return;
    const read = () => setRemoteFeed(feedSizeOf(el));
    el.addEventListener('loadedmetadata', read);
    el.addEventListener('resize', read);
    read();
    return () => { el.removeEventListener('loadedmetadata', read); el.removeEventListener('resize', read); };
  }, [mainDisplayStream]);
  const paneRef = useRef<HTMLDivElement>(null);
  const [pane, setPane] = useState<Box>({ w: 0, h: 0 });
  useEffect(() => {
    const el = paneRef.current;
    if (!el) return;
    const read = () => setPane({ w: el.clientWidth, h: el.clientHeight });
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // The content area, and the column's slot for the match while a share has the stage (144): the
  // slot's box in the content area, read again whenever either resizes.
  const contentRef = useRef<HTMLDivElement>(null);
  const [slotEl, slotRef] = useState<HTMLDivElement | null>(null);
  const [slotBox, setSlotBox] = useState<(Box & { x: number; y: number }) | null>(null);
  useEffect(() => {
    const content = contentRef.current;
    if (!slotEl || !content) { setSlotBox(null); return; }
    const read = () => {
      const a = slotEl.getBoundingClientRect();
      const c = content.getBoundingClientRect();
      setSlotBox({ x: a.left - c.left, y: a.top - c.top, w: a.width, h: a.height });
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(slotEl);
    ro.observe(content);
    return () => ro.disconnect();
  }, [slotEl]);
  // The call's own chat; on a phone, a sheet over the lower part of the pane.
  const [chatOpen, setChatOpen] = useState(false);

  // Diagnostics (30 Oct): "d" on a keyboard, or three taps on the timer, shows each peer's stats and
  // track state; the record goes under the session every 10 s either way.
  const [diagOpen, setDiagOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'd' || e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      setDiagOpen((o) => !o);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const timerTaps = useRef<number[]>([]);
  const tapTimer = () => {
    const now = Date.now();
    timerTaps.current = [...timerTaps.current.filter((t) => now - t < 700), now];
    if (timerTaps.current.length >= 3) { timerTaps.current = []; setDiagOpen((o) => !o); }
  };
  const pageLoad = usePageLoad(diagOpen);
  const diagnostics = useCallDiagnostics({
    open: diagOpen, from: participantId, bookingId, sessionId: booking?.roomSessionId,
    side: inviteToken ? undefined : isCoach ? 'coach' : 'player',
    peers: participants.filter((x) => !x.isLocal).map((x) => ({ id: x.id, name: x.displayName, streams: x.streams })),
    mainPeer: remoteParticipant?.id, videoEl: () => remoteVideoRef.current,
  });
  const chatSheetUp = chatOpen && !large;
  // The stage (stageBox): dark, the call's whole width, the feed letterboxed inside it;
  // 8 in with radius 16 on a larger screen, edge to edge on a phone; above the chat sheet while it is up.
  const stage = stageBox(pane, large, chatSheetUp);
  const tileStyle = pane.w && pane.h
    ? { width: stage.w, height: stage.h, left: stage.x, top: stage.y, borderRadius: stage.radius }
    : undefined;
  // A phone with the match or a share on its stage (decision 162, docs/specs/room-layout.md): the main
  // picture, the match's place and the tiles from phoneStage, in portrait (a strip under, touching) and on
  // its side (a column at the right, or the top right corner over a full-height match). Desktop as before.
  const phonePortrait = useMatch('(orientation: portrait)');
  // A phone on its side: a touch screen in landscape, or any landscape window under 500 tall.
  const shortLandscape = useMatch('(orientation: landscape) and (max-height: 500px)');
  // The three as one query, so a turn flips it once: read from the three apart, a turn passed through
  // "not a phone" for a render (wide before landscape), and the match's chapters panel opened for it.
  const phoneRoom = useMatch('(max-width: 767.98px), (pointer: coarse) and (orientation: landscape), (orientation: landscape) and (max-height: 500px)');
  const [matchShape, setMatchShape] = useState<{ aspect: number | null; sideBySide: boolean }>({ aspect: null, sideBySide: false });
  const phoneLay = phoneRoom && sidebarActive && inCall && pane.w > 0 && pane.h > 0 ? phoneStage({
    portrait: phonePortrait && !immersive && !shortLandscape,
    area: pane,
    match: matchOpen ? { aspect: matchShape.aspect ?? 16 / 9, sideBySide: matchShape.sideBySide } : null,
    share: layout.shareOnStage ? { aspect: !layout.ownShareOnStage && remoteFeed?.w && remoteFeed.h ? remoteFeed.w / remoteFeed.h : 16 / 9 } : null,
    camerasShown: !(matchOpen && camerasHiddenNow),
    tiles: [themAspect, localFeed?.w && localFeed.h ? localFeed.w / localFeed.h : 9 / 16],
  }) : null;
  const rectStyle = (r: Rect, radius = 12) => ({ left: r.x, top: r.y, width: r.w, height: r.h, borderRadius: radius });
  // A phone's controls (162, utils/callChrome): on a touch screen the call's bar and the tools rail hide after
  // 3 s without a touch; a tap on the stage shows or hides them, and the tap that shows them does nothing else.
  const touchPhone = useMatch('(pointer: coarse)') && (!large || immersive);
  const [chromeShown, setChromeShown] = useState(true);
  const chromeShownRef = useRef(true);
  useEffect(() => { chromeShownRef.current = chromeShown; }, [chromeShown]);
  const [chromeTouch, setChromeTouch] = useState(0);
  const chromeHeld = chatOpen || diagOpen || !!moreMenu || showRecommend || showInviteModal || !!viewingOffer || !!heldOffer;
  useEffect(() => {
    if (!touchPhone || !inCall || !chromeShown || chromeHeld) return;
    const id = setTimeout(() => setChromeShown(false), CHROME_HIDE_MS);
    return () => clearTimeout(id);
  }, [touchPhone, inCall, chromeShown, chromeHeld, chromeTouch]);
  useEffect(() => {
    const el = roomRef.current;
    if (!el || !touchPhone || !inCall) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      const next = chromeTap({ shown: chromeShownRef.current, onControl: !!target?.closest(CONTROL_SELECTOR) });
      setChromeTouch((n) => n + 1);
      if (next.shown !== chromeShownRef.current) setChromeShown(next.shown);
      if (!next.swallow) return;
      e.preventDefault();
      e.stopPropagation();
      // The click this tap makes goes nowhere either.
      const eat = (c: Event) => { c.preventDefault(); c.stopPropagation(); };
      window.addEventListener('click', eat, { capture: true, once: true });
      setTimeout(() => window.removeEventListener('click', eat, { capture: true }), 600);
    };
    el.addEventListener('pointerdown', onDown, { capture: true });
    return () => el.removeEventListener('pointerdown', onDown, { capture: true });
  }, [touchPhone, inCall]);
  const chromeHidden = touchPhone && inCall && !chromeShown;
  // The share's stage at its place in the phone's layout, the strip under it touching it.
  // With the match on a phone's stage, the stage's own feed is not shown around it (their camera is a tile),
  // but its element keeps playing their voice: hidden, not removed.
  const shareStyle = phoneLay && layout.shareOnStage ? rectStyle(phoneLay.main)
    : phoneLay && tileStyle ? { ...tileStyle, visibility: 'hidden' as const } : tileStyle;

  // The self tile: small in a corner of the stage (141, 147), bottom right by default,
  // dragged to any of the four; a top one under the banner, chips and toast; inside the stage in
  // landscape. The corner holds for the browser session, a rejoin included.
  const selfShown = !!localStream && !sidebarActive;
  const selfSize = selfTileSize(localFeed, stage, stage.h * (large ? 0.3 : 0.45), large);
  const [selfCorner, setSelfCorner] = useState<Corner>(() => storedSelfCorner(sessionStore()));
  const moveSelf = useCallback((c: Corner) => { setSelfCorner(c); storeSelfCorner(sessionStore(), c); }, []);
  const nameLabel = nameLabelPlace({ stageW: stage.w, selfShown: selfShown && !threeTiles, selfW: selfSize.w, selfCorner });
  // The banner's height, so what sits along the top goes below it while it shows.
  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerH, setBannerH] = useState(0);
  useEffect(() => {
    const el = bannerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBannerH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  });
  const topInset = bannerOffer && !chatSheetUp && bannerH ? bannerH + 8 : 0;
  // The chips' height (0 with none up, or hidden under the chat sheet), so a top corner's tile stands under them.
  const chipsRef = useRef<HTMLDivElement>(null);
  const [chipsH, setChipsH] = useState(0);
  useEffect(() => {
    const el = chipsRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setChipsH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  });
  const selfTop = selfTopInset({ banner: topInset ? bannerH : 0, chips: chatSheetUp ? 0 : chipsH });

  const [storedChat, setStoredChat] = useState<RoomChatLine[]>([]);
  // Lines that came over the data channel (and this side's own), shown before the room's listener has them.
  const [liveChat, setLiveChat] = useState<RoomChatLine[]>([]);
  useEffect(() => onData((_from, data) => {
    if (isChatWire(data)) setLiveChat((l) => (l.some((x) => x.id === data.line.id) ? l : [...l, data.line]));
  }), [onData]);
  // The newest line seen with the chat open, kept for the browser session so
  // a rejoin does not count what was already read as new.
  const seenKey = `db_call_chat_seen_${roomId}`;
  const [chatSeen, setChatSeen] = useState<string | null>(() => {
    try { return sessionStorage.getItem(seenKey); } catch { return null; }
  });
  const [chatSession, setChatSession] = useState<string | null>(null);
  // A lesson's chat is on its session (decision 132: the server posts a line to the thread while only one is in).
  useEffect(() => subscribeRoomChat(roomId, bookingId, sessionId, (lines, session) => { setStoredChat(lines); setChatSession(session); }), [roomId, bookingId, sessionId]);
  const chat = useMemo(() => mergeChatLines(storedChat, liveChat, chatSession), [storedChat, liveChat, chatSession]);
  const newestAt = chat.length ? chat[chat.length - 1].at : null;
  useEffect(() => {
    if (!chatOpen || !newestAt) return;
    setChatSeen(newestAt);
    try { sessionStorage.setItem(seenKey, newestAt); } catch { /* this call only */ }
  }, [chatOpen, newestAt, seenKey]);
  const unread = chatOpen ? 0 : unreadCount(chat, participantId, chatSeen);

  // The red button is Leave, for both roles, with no question (decision 54).
  // It leaves this device at once, whatever the network does next; the call
  // stays open for the other person, and nobody ends the lesson from here.
  const left = useRef(false);
  const leaveNow = useCallback(() => {
    if (left.current) return;
    left.current = true;
    const plan = endCallPlan({ booked: !!bookingId });
    localStream?.getTracks().forEach((t) => t.stop());
    // The other side's presence and banner clear on this, whatever the wrapper's leave does next:
    // the Leave and its time on this side's entry (withEntryLeave reads it), written before the
    // left screen shows, so both sides read the same thing (at most 1.5 s, then on regardless).
    const leaveWritten = updateDoc(doc(db, 'webrtc_rooms', roomId, 'participants', participantId),
      { active: false, sharing: false, leftAt: serverTimestamp() }).catch(() => { /* best-effort */ });
    if (plan.closeRoom) {
      updateDoc(doc(db, 'webrtc_rooms', roomId), { status: 'ended' }).catch(() => { /* non-fatal */ });
    }
    // Closing unmounts the provider, whose teardown leaves the room and stops
    // any share once; a second leave here raced it to a rejected write.
    void Promise.race([leaveWritten, new Promise((r) => setTimeout(r, 1500))])
      .then(() => onClose({ how: 'leave' }));
  }, [bookingId, localStream, onClose, roomId, participantId]);
  // More: what is left for it, the player's invite (coaches have none, decision 62). Message and the
  // match are buttons of their own. While guests by link are not open (GUEST_JOIN off), no invite, so no More.
  const moreItems = moreHasInvite({ guestJoin: GUEST_JOIN, booked: !!bookingId }) && !isCoach
    ? [{ key: 'invite', label: notice.invite, icon: <UserPlus size={18} />, onSelect: () => setShowInviteModal(true) }]
    : [];

  const isScheduled = !!bookingId;
  // The bar's buttons, left to right (callBar).
  const bar = barButtons({
    isCoach: !!isCoach, booked: isScheduled && !!booking, canShare: canShareScreen, theyAreIn,
    hasOther: !!otherParticipantId, offers: offers.length, hasMatch: hasVideo, guestJoin: GUEST_JOIN, matchOpen,
  });

  const groups = barGroups(bar, large);
  // Meet's glyphs: 20 in a 40 control on a larger screen, 24 in a phone's.
  const glyph = large ? 20 : 24;
  const barButton = (b: BarButton, corner: boolean): React.ReactNode => {
    switch (b) {
      case 'mic':
        return (
          <CallButton onPress={() => { toggleAudio(); sendData({ t: 'av', mic: !audioEnabled } satisfies AvWire); }} off={!audioEnabled}
            ariaLabel={audioEnabled ? 'Mute microphone' : 'Unmute microphone'}>
            {audioEnabled ? <Microphone size={glyph} weight="fill" /> : <MicrophoneSlash size={glyph} weight="fill" />}
          </CallButton>
        );
      case 'camera':
        return (
          <CallButton onPress={() => { toggleVideo(); sendData({ t: 'av', cam: !videoEnabled } satisfies AvWire); }} off={!videoEnabled}
            ariaLabel={videoEnabled ? 'Turn camera off' : 'Turn camera on'}>
            {videoEnabled ? <VideoCamera size={glyph} weight="fill" /> : <VideoCameraSlash size={glyph} weight="fill" />}
          </CallButton>
        );
      case 'share':
        // Phones cannot share a screen; a narrow window keeps the bar short.
        return (
          <CallButton
            className="hidden md:flex"
            onPress={() => {
              if (isSharing) stopScreenShare();
              else void startScreenShare('Screen').catch(() => { /* picker dismissed */ });
            }}
            tone={isSharing ? 'active' : 'fill'}
            ariaLabel={isSharing ? 'Stop sharing your screen' : 'Share your screen'}
          >
            <MonitorArrowUp size={glyph} weight="fill" />
          </CallButton>
        );
      case 'chat':
        return (
          <CallButton onPress={() => setChatOpen((o) => !o)} tone={chatOpen ? 'active' : corner ? 'plain' : 'fill'} corner={corner}
            ariaLabel={chatOpen ? 'Close chat' : unread ? `Chat, ${unread} new` : 'Chat'} badge={unread}>
            {unread ? <ChatCircleText size={24} weight="fill" /> : <ChatCircle size={24} weight="fill" />}
          </CallButton>
        );
      case 'recommend':
        return (
          <CallButton onPress={() => setShowRecommend(true)} tone={showRecommend ? 'active' : 'fill'} ariaLabel="Recommend">
            <Star size={glyph} weight="fill" />
          </CallButton>
        );
      case 'match':
        // The match, the coach's own button: Open Match opens it, the same button closes it.
        return (
          <CallButton onPress={() => setCoachMatch(!showVideoPanel)} tone={showVideoPanel ? 'active' : 'fill'}
            ariaLabel={showVideoPanel ? 'Close Match' : 'Open Match'}>
            <FilmSlate size={glyph} weight="fill" />
          </CallButton>
        );
      case 'more':
        return moreItems.length > 0 ? (
          <CallButton onPress={() => {}} onPressAt={(r) => setMoreMenu({ x: r.left + r.width / 2, y: r.top - 8 })} ariaLabel="More">
            <DotsThreeVertical size={glyph} weight="bold" />
          </CallButton>
        ) : null;
      case 'leave':
        // Leave, always: before the call is live there is still this device to leave.
        return (
          <CallButton onPress={leaveNow} tone="leave" ariaLabel="Leave" wide>
            <PhoneDisconnect size={glyph} weight="fill" />
          </CallButton>
        );
    }
  };

  // The column's or the phone layout's two camera tiles, theirs and this side's.
  const themTile = (cls: string, style: React.CSSProperties | undefined) => (
              <div className={cls} style={style} data-camera-tile="them">
                <video
                  ref={sidebarRemoteVideoCallback}
                  autoPlay
                  playsInline
                  className="h-full w-full object-contain"
                />
                {remoteCameraOff && (
                  <div className="absolute inset-0 flex items-center justify-center bg-call-tile">
                    <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-call-canvas">
                      {otherIdentity?.avatarUrl
                        ? <img src={otherIdentity.avatarUrl} alt={otherParticipantName} className="h-full w-full object-cover" onError={hideBroken} />
                        : <User size={24} className="text-white/50" aria-hidden="true" />}
                    </div>
                  </div>
                )}
                <NameLabel name={otherFirst} micOff={remoteMuted} className="absolute bottom-2 left-2" />
              </div>
  );
  const youTile = (cls: string, style: React.CSSProperties | undefined) => (
              <div className={cls} style={style} data-camera-tile="you">
                {videoEnabled && localStream ? (
                  <video
                    ref={sidebarLocalVideoCallback}
                    autoPlay
                    playsInline
                    muted
                    className="h-full w-full object-contain"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <User size={24} className="text-white/50" aria-hidden="true" />
                  </div>
                )}
                <NameLabel name="You" micOff={!audioEnabled} micOffLabel="Your microphone is off" className="absolute bottom-2 left-2" />
              </div>
  );

  // Rule 9: a call outside any lesson, until the other side accepts.
  if (waitingToAccept) return <WaitingToAcceptScreen other={otherFirst} onLeave={leaveNow} />;

  const selfView = (
    <>
      {videoEnabled && localStream ? (
        <video
          ref={localVideoCallback}
          autoPlay
          playsInline
          muted
          className="pointer-events-none h-full w-full object-cover"
          style={{ transform: 'scaleX(-1)' }}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <User size={28} className="text-white/50" aria-hidden="true" />
        </div>
      )}
      <NameLabel name="You" micOff={!audioEnabled} micOffLabel="Your microphone is off" className="absolute bottom-2 left-2" />
    </>
  );

  const chatPanel = (
    <RoomChatPanel lines={chat} me={participantId} onClose={() => setChatOpen(false)}
      onSend={async (text) => {
        const line = await sendRoomChat(roomId, chatSession, { from: participantId, name: displayName, text },
          bookingId && sessionId ? { bookingId, sessionId } : undefined);
        if (!line) return;
        setLiveChat((l) => [...l, line]);
        sendData(chatWire(line));
      }} />
  );

  return (
    <div
      // The call takes over the screen above the app's own chrome. At z-50 it
      // tied with the sticky top bar, and the phone tab bar is 9999
      // (#mobile-bottom-nav, index.css), so opened from the incoming banner
      // it sat under them and the tab bar covered End. The dialog layer
      // (10200) clears both and the upload pills; toasts stay above.
      ref={roomRef}
      className={`fixed z-[var(--z-editor-dialog)] bg-call-bezel ${chrome.fill ? 'room-fill inset-x-0' : 'inset-0'}`}
      data-immersive={immersive || undefined}
      data-room-fill={chrome.fill || undefined}
      data-room-channel={channelOpen ? 'open' : undefined}
      style={{
        // A vertical swipe is Safari's to collapse its bars where the room cannot go full screen.
        touchAction: chrome.fill ? 'pan-y' : 'none',
        // The room keeps clear of the notch, the home indicator and, in
        // landscape, the sensor housing (DESIGN_SYSTEM LC-1).
        paddingTop: 'env(safe-area-inset-top)',
        paddingRight: 'env(safe-area-inset-right)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
      }}
    >
      {/* The editor's one-time hint, top centre, Subhead 15/20. */}
      {chrome.hint !== 'off' && (
        <div role="status"
          className="call-glass pointer-events-none fixed left-1/2 z-30 flex h-11 w-max max-w-[calc(100vw-32px)] -translate-x-1/2 items-center rounded-full px-4 text-center text-[15px] leading-[20px] font-semibold text-white"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)', opacity: chrome.hint === 'on' ? 1 : 0, transition: 'opacity 300ms ease' }}
          data-bars-hint>
          {ROOM_BARS_HINT}
        </div>
      )}
      {/* Hidden audio element — plays the coach's video-playback audio on the player side */}
      <audio ref={videoAudioCallback} autoPlay playsInline style={{ display: 'none' }} />

      {/* The call, Meet's way (redesign spec, batch 5): a thin strip on top,
          the tiles, a full-width bar of round controls at the bottom. */}
      <div className="flex h-full w-full flex-row">
      {/* The call: the strip, the stage and the bar; on a larger screen the chat's column beside it, full height. */}
      <div className="flex h-full min-w-0 flex-1 flex-col">
        {/* ── Top strip, on a phone only: the lesson left, elapsed time and REC right. On a larger screen they sit
            at the bar's left, as Meet's (141). Gone in a phone's landscape and while the match is open: nothing above the video. ── */}
        {!large && (
        <div className={`h-11 flex-shrink-0 items-center gap-3 bg-white/[0.06] px-4 text-white ${immersive || matchOpen ? 'hidden' : 'flex'}`}>
          {/* The lesson's whole title: two lines in the strip's 44, stepped down past that. */}
          <FitText lines={2} className="flex-1 text-[15px] leading-[18px] font-medium">
            {booking ? [liveLessonTitle(booking), monthLessonMode(booking)].filter(Boolean).join(' · ') : otherParticipantName}
          </FitText>
          {inCall && joinedAt && (
            <span className="flex-shrink-0 text-[13px] leading-[18px] tabular-nums text-white/70 select-none" onClick={tapTimer} data-call-timer>
              {timeLabel(new Date(clock).toISOString(), viewerZone)}
            </span>
          )}
          {recording.active && recording.enabled && <RecordingDot />}
        </div>
        )}

        {/* The stage fills the width on the black bezel, with or without the match (141, 104). */}
        <div className="flex min-h-0 w-full flex-1 flex-row">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* ── Content area: in sidebar mode the camera rail sits under the
            clip in portrait and beside it in landscape; a column otherwise ─── */}
        <div ref={contentRef}
          className={`relative flex flex-1 overflow-hidden min-h-0 ${phoneMatchRail ? 'flex-row' : `flex-col ${sidebarActive ? 'landscape:flex-row' : ''}`}`}
        >
          {/* ── Main pane ───────────────────────────────────────────────────── */}
          <div ref={paneRef} className="flex-1 relative overflow-hidden min-h-0 min-w-0">
            {/* The remote tile at its feed's own aspect, 8 in from the pane, radius 12 (Meet, 141). */}
            <div className={`absolute overflow-hidden bg-call-tile ${shareStyle ? '' : 'inset-0'}`} style={shareStyle} data-remote-tile data-feed={remoteFeed ? `${remoteFeed.w}x${remoteFeed.h}` : undefined}>
              {/* Remote video — always mounted so srcObject never races against mount */}
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="h-full w-full object-contain"
              />

              {/* Not on camera: their photo, and one line for their state (roomPresence). */}
              {!onCamera && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-call-tile px-8 text-center" data-their-state={shownState}
                  style={{ paddingTop: topInset }}
                  {...(remoteCameraOff ? { 'data-camera-off': '' } : {})}>
                  <div className="flex h-28 w-28 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-call-canvas md:h-36 md:w-36">
                    {otherIdentity?.avatarUrl
                      ? <img src={otherIdentity.avatarUrl} alt={otherParticipantName} className="h-full w-full object-cover" onError={hideBroken} />
                      : <User size={48} className="text-white/50" aria-hidden="true" />}
                  </div>
                  {/* Camera off: the avatar centred, "Camera off" small under it; any other state its line. */}
                  {stageLine && <p className={`${remoteCameraOff ? 'text-[13px] leading-[18px] text-white/65' : 'text-[15px] leading-[20px] text-white/80'}`} data-stage-line>{stageLine}</p>}
                  {noShowNow?.decide && bookingId && booking && !joinError && (
                    <NoShowDecision bookingId={bookingId} playerFirst={otherFirst} member={!!booking.membershipId} />
                  )}
                </div>
              )}

              {/* The other side's name, plain text in the stage's bottom corner (141), their mic struck through
                  beside it while muted, on the side away from the self tile and short of it, so the tile never
                  covers it (nameLabelPlace). */}
              {onCamera && (
                <span className={`pointer-events-none absolute bottom-3 flex items-center gap-1.5 font-medium text-white select-none [text-shadow:0_1px_2px_rgba(0,0,0,0.6)] ${large ? 'text-[15px] leading-[20px]' : 'text-[13px] leading-[18px]'} ${nameLabel.side === 'left' ? 'left-3' : 'right-3'}`}
                  style={{ maxWidth: nameLabel.maxWidth }} data-name-label>
                  {effectiveCanvasStream
                    ? <MonitorArrowUp size={16} weight="fill" aria-hidden="true" />
                    : remoteMuted && <MuteBadge label="Microphone off" />}
                  <span className="min-w-0 [overflow-wrap:anywhere]">{effectiveCanvasStream ? shareLabel(otherFirst) : otherParticipantName}</span>
                </span>
              )}
            </div>

            {/* This side's own share (144): its stage says so, with Stop Sharing, as Meet's presenter sees;
                the cameras and any match are in the column. */}
            {layout.ownShareOnStage && (
              <div role="status" className={`absolute z-[6] flex flex-col items-center justify-center gap-4 bg-call-tile px-8 text-center text-white ${tileStyle ? '' : 'inset-0'}`}
                style={shareStyle} data-sharing-chip>
                <MonitorArrowUp size={40} weight="fill" aria-hidden="true" />
                <p className="text-[20px] leading-[25px] font-semibold">You're sharing your screen</p>
                <button type="button" onClick={stopScreenShare}
                  className="ios-press flex h-12 items-center rounded-full bg-white px-6 text-[17px] leading-[22px] font-medium text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
                  Stop Sharing
                </button>
              </div>
            )}

            {/* ── Along the top, clear of the self tile: the player's recommendation banner, then the chips
                (the end reminder and Booked, each closable; this side's share is on its stage, 144). Not over the small
                tile above the phone's chat sheet; back when it closes. ── */}
            {/* The player's recommendation: a banner the stage's full width along the top; the self tile and chips sit below it. */}
            {bannerOffer && !chatSheetUp && (
              <div ref={bannerRef} className="pointer-events-none absolute inset-x-3 top-3 z-10 flex justify-center">
                <div role="status" className="call-glass pointer-events-auto flex w-full max-w-[520px] items-center gap-2 rounded-[16px] py-1.5 pl-4 pr-1.5" data-offer-banner>
                  <span className="min-w-0 flex-1 text-[15px] leading-[20px] [overflow-wrap:anywhere]">
                    {otherFirst} recommends {OFFER_TITLE[bannerOffer.offer]}
                  </span>
                  <button type="button" onClick={() => setViewingOffer(bannerOffer.offer)}
                    className="ios-press flex h-9 flex-shrink-0 items-center rounded-full bg-white/[0.14] px-3.5 text-[15px] leading-[20px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
                    View
                  </button>
                  <ChipClose label="Close the recommendation" onClose={() => closeOffer(bannerOffer.offer)} />
                </div>
              </div>
            )}
            <div ref={chipsRef} className={`pointer-events-none absolute z-10 flex flex-col items-start gap-2 ${chatSheetUp ? 'hidden' : ''}`}
              style={{ left: CORNER_INSET, right: CORNER_INSET, top: CORNER_INSET + topInset }} data-top-chips>
              <div className="flex flex-wrap items-start gap-2">
              {lastChip && chipsClosed.reminder !== lastChip.title && (
                <div
                  role="status"
                  aria-hidden={!endChip}
                  onTransitionEnd={() => { if (!endChip) setLastChip(null); }}
                  className="call-glass pointer-events-auto flex min-w-0 max-w-full items-start gap-1 rounded-2xl py-1.5 pl-3 pr-1 select-none"
                  style={{ opacity: endChip ? 1 : 0, transition: 'opacity 600ms ease' }}
                  data-end-chip
                >
                  {/* Wraps inside the stage on a phone rather than running off its edge. */}
                  <span className="flex min-w-0 flex-col">
                    <span className="text-[15px] leading-[20px] font-medium">{lastChip.title}</span>
                    {lastChip.detail && <span className="text-[13px] leading-[18px] text-[rgba(255,255,255,0.65)]">{lastChip.detail}</span>}
                  </span>
                  <ChipClose label="Close the reminder" onClose={() => setChipsClosed((c) => ({ ...c, reminder: lastChip.title }))} />
                </div>
              )}
              {bookedOffer && !chipsClosed.booked && (
                <span className="call-glass pointer-events-auto flex h-8 flex-shrink-0 items-center gap-1.5 rounded-full pl-3 pr-1 text-[15px] leading-[20px] font-medium select-none" data-booked-chip>
                  <CheckCircle size={16} weight="fill" color="#34C759" aria-hidden="true" />
                  Booked
                  <ChipClose label="Close" onClose={() => setChipsClosed((c) => ({ ...c, booked: true }))} />
                </span>
              )}
              </div>
            </div>

            {/* ── Three people (a guest in): three tiles, each feed at its own aspect, each named ── */}
            {threeTiles && (
              <div className="absolute inset-0 z-[5] grid grid-rows-3 gap-2 bg-call-canvas p-2 landscape:grid-cols-3 landscape:grid-rows-1 md:grid-cols-3 md:grid-rows-1" data-three-tiles>
                {/* Their voice already plays from the stage's element: muted here. */}
                <GridTile stream={remoteCameraStream} name={otherFirst} muted micOff={remoteMuted} />
                {otherPeople.map((p) => (
                  <GridTile key={p.id} stream={p.streams.find((x) => x.getVideoTracks().length > 0) ?? null}
                    name={p.displayName || 'Guest'} />
                ))}
                <GridTile stream={localStream ?? null} name="You" muted mirrored micOff={!audioEnabled} />
              </div>
            )}

            {/* ── Self view: live as soon as there is a camera, sized by its feed; the rail has it otherwise */}
            {selfShown && !threeTiles && (
              <SelfTile bounds={stage} origin={stage} top={selfTop} size={selfSize} corner={selfCorner} onCorner={moveSelf}>{selfView}</SelfTile>
            )}

            {diagOpen && <CallDiagnostics {...diagnostics} notes={[
              `Restarts: stage ${stageRestarts} · tile ${tileRestarts}`,
              `Budget: ${budgetIsOn ? (phoneDevice ? 'on, phone' : 'on') : 'off'} · they ask low: ${farWantsLow ? 'yes' : 'no'}`,
              ...(pageLoad ? [pageLoad] : []),
            ]} onClose={() => setDiagOpen(false)} />}

            {/* ── The recording notice: a toast at the top of the stage, 3 seconds (decision 73) ── */}
            {toast === 'shown' && (
              <div role="status" data-recording-toast
                className="recording-toast pointer-events-none absolute left-1/2 top-3 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/80 px-4 py-2 text-[15px] leading-[20px] font-medium text-white">
                <span className="h-2 w-2 flex-shrink-0 rounded-full bg-white" aria-hidden="true" />
                {RECORDING_TOAST}
              </div>
            )}

            {/* ── The chat on a phone: a sheet over the tiles ─────────────────── */}
            {chatOpen && !large && (
              <div className="absolute inset-x-0 bottom-0 z-20 overflow-hidden rounded-t-[16px]" style={{ height: `${CHAT_SHEET_SHARE * 100}%` }}>{chatPanel}</div>
            )}
          </div>

          {/* ── Camera rail (Google Meet-style): a strip of two tiles under the
              clip in portrait; beside it in landscape, 16:9 tiles, 96 wide on
              a phone and 240 on a larger screen. Tiles on the call tile,
              radius 12, 8 apart (LC-1). ─────────────────────────────────── */}
          {sidebarActive && !camerasOff && !phoneLay && (
            <div data-camera-tiles className={phoneMatchRail
              ? 'flex w-24 flex-shrink-0 flex-col gap-2 overflow-hidden py-2 pr-2'
              // With the match in it (144), room for the match's own bar: 112 high upright on a phone, 160 wide on
              // its side, 320 on a larger screen.
              : `flex flex-shrink-0 flex-row gap-2 px-2 pb-2 landscape:h-auto landscape:flex-col landscape:py-2 landscape:pl-0 landscape:pr-2 ${matchInColumn ? 'h-28 landscape:w-40 [@media(min-width:768px)_and_(min-height:501px)]:landscape:w-80' : 'h-24 landscape:w-24 [@media(min-width:768px)_and_(min-height:501px)]:landscape:w-60'}`}>
              {/* With a share on the stage, the match's place at the top of the column (144): the match itself
                  stays one player, moved over this slot, so the share never restarts or closes it. */}
              {matchInColumn && (
                <div ref={slotRef} className="aspect-video h-full flex-none rounded-[12px] bg-call-tile landscape:h-auto landscape:w-full" data-match-slot />
              )}
              {themTile(phoneMatchRail ? 'relative w-full flex-none overflow-hidden rounded-[12px] bg-call-tile' : 'relative min-w-0 flex-1 overflow-hidden rounded-[12px] bg-call-tile landscape:aspect-video landscape:flex-none',
                phoneMatchRail ? { aspectRatio: tileAspect(remoteFeed, 16 / 9) } : undefined)}
              {youTile(phoneMatchRail ? 'relative w-full flex-none overflow-hidden rounded-[12px] bg-call-tile' : 'relative min-w-0 flex-1 overflow-hidden rounded-[12px] bg-call-tile landscape:aspect-video landscape:flex-none',
                phoneMatchRail ? { aspectRatio: tileAspect(localFeed, 9 / 16) } : undefined)}
            </div>
          )}
          {/* A phone's tiles where its layout puts them (decision 162): a strip under the picture, a column at
              the right, or over the top right corner; with a share, the match's place comes first. */}
          {phoneLay?.tiles && (
            <div data-camera-tiles data-tiles-form={phoneLay.tiles.form} className="pointer-events-none absolute inset-0 z-10">
              {(() => {
                const cams = phoneLay.tiles.items.slice(layout.shareOnStage && matchOpen ? 1 : 0);
                const tileCls = 'pointer-events-auto absolute overflow-hidden bg-call-tile';
                return <>
                  {cams[0] && themTile(tileCls, rectStyle(cams[0]))}
                  {cams[1] && youTile(tileCls, rectStyle(cams[1]))}
                </>;
              })()}
            </div>
          )}
        {/* The match video, the editor's player in sync mode, one player for the call: over the stage, or with a
            share on the stage over the column's slot (144). Moved, never mounted again, so it keeps playing. */}
        {matchOpen && bookingId && matchUrl && (
          <div className={`absolute z-[9] overflow-hidden ${matchInColumn ? 'rounded-[12px]' : ''}`}
            style={phoneLay?.match ? rectStyle(phoneLay.match, layout.shareOnStage ? 12 : 0)
              : matchInColumn && slotBox ? { left: slotBox.x, top: slotBox.y, width: slotBox.w, height: slotBox.h } : { left: 0, top: 0, width: pane.w, height: pane.h }}
            data-match-host={matchInColumn ? 'column' : 'stage'}>
            <RoomMatchPlayer
              isCoach={!!isCoach}
              videoUrl={matchUrl}
              playerName={booking?.playerName}
              otherFirst={otherFirst}
              coachFirst={isCoach ? (booking.coachName ?? displayName).split(' ')[0] : otherFirst}
              phone={phoneRoom || matchInColumn}
              onShape={setMatchShape}
              stageFullscreen={{ on: stageFull, toggle: toggleStageFull }}
              chromeHidden={chromeHidden}
              tools={!matchInColumn}
              hideSecond={budgetIsOn && phoneDevice && matchInColumn}
              chapters={lessonChapters(booking)}
              onChaptersChange={isCoach && bookingId ? (next) => {
                saveLiveChapters(bookingId, next).catch(() => showError('Could not save the chapter. Please try again.'));
              } : undefined}
              session={matchSession}
              initial={sessionMatch}
              onRestored={() => setSessionMatch(null)}
              cameras={matchInColumn ? undefined : { hidden: camerasHidden, onToggle: () => setCamerasHidden((v) => !v) }}
            />
          </div>
        )}

        </div>
        </div>
        </div>

        {/* ── The bar, grouped as Meet's (141): on a larger screen the time and the lesson at the left, round 40
            controls in the middle ending in the red Leave, Chat in the right corner; on a phone one centred row
            of up to 56 each. No labels: each button's name is its tooltip (callBar). ── */}
        <div role="toolbar" aria-label="Call controls"
          className={`flex flex-shrink-0 items-center bg-call-bezel transition-opacity duration-200 ${large ? 'h-20 gap-3 px-6' : 'justify-center px-4 pb-4 pt-3'} ${chromeHidden ? 'opacity-0' : ''}`}
          data-call-bar data-chrome-hidden={chromeHidden ? '' : undefined}>
          {large && (
            <div className="flex min-w-0 flex-1 items-center gap-3 text-white" data-bar-info>
              {inCall && joinedAt && (
                <>
                  <span className="flex-shrink-0 text-[15px] leading-[20px] font-medium tabular-nums select-none" onClick={tapTimer} data-call-timer>
                    {timeLabel(new Date(clock).toISOString(), viewerZone)}
                  </span>
                  <span className="h-4 w-px flex-shrink-0 bg-white/30" aria-hidden="true" />
                </>
              )}
              <FitText lines={2} className="min-w-0 flex-initial text-[15px] leading-[20px] font-medium">
                {booking ? [liveLessonTitle(booking), monthLessonMode(booking)].filter(Boolean).join(' · ') : otherParticipantName}
              </FitText>
              {recording.active && recording.enabled && <RecordingDot />}
            </div>
          )}
          <div className={`flex min-w-0 items-center justify-center ${large ? 'gap-3' : 'w-full gap-1.5'}`} data-bar-controls>
            {groups.center.map((b) => <React.Fragment key={b}>{barButton(b, false)}</React.Fragment>)}
          </div>
          {large && (
            <div className="flex min-w-0 flex-1 items-center justify-end gap-2" data-bar-corner>
              {groups.corner.map((b) => <React.Fragment key={b}>{barButton(b, true)}</React.Fragment>)}
            </div>
          )}
        </div>
      </div>
      {/* ── The chat on a larger screen: a column down the right, full height; the stage gives up its width (Meet) ── */}
      {chatOpen && large && <div className="flex h-full w-[360px] flex-shrink-0 flex-col py-2 pr-2" data-chat-column>{chatPanel}</div>}
      </div>

      {moreMenu && (
        <ContextMenu
          anchor={moreMenu}
          onClose={() => setMoreMenu(null)}
          // Above the call, which sits on the dialog layer (10200).
          className="z-[10300]!"
          ariaLabel="More"
          groups={[{ key: 'more', items: moreItems }]}
        />
      )}

      {/* ── Invite modal: the player's invite to their own lesson ───────────── */}
      {showInviteModal && bookingId && (
        <CallInviteModal
          bookingId={bookingId}
          playerId={isCoach ? (otherParticipantId ?? '') : participantId}
          coachId={isCoach ? participantId : (otherParticipantId ?? '')}
          title={notice.invite}
          minor={notice.minor}
          canEmail={!!isCoach}
          prefetched={invites}
          onClose={() => setShowInviteModal(false)}
        />
      )}

      {showRecommend && bookingId && booking && (
        <RecommendSheet
          bookingId={bookingId}
          coachId={participantId}
          playerId={otherParticipantId}
          playerFirstName={otherParticipantName.split(' ')[0] || otherParticipantName}
          offers={offers}
          recommendations={recommendations}
          onClose={() => setShowRecommend(false)}
          onSent={() => setShowRecommend(false)}
          onSending={(offer, note, priceCents) => sendData({ t: 'rec', on: true, offer, note, priceCents, sentAt: new Date().toISOString() } satisfies RecommendWire)}
          onFailed={(offer) => sendData({ t: 'rec', on: false, offer, note: '', priceCents: 0, sentAt: new Date().toISOString() } satisfies RecommendWire)}
        />
      )}

      {/* The floating window while the tab is in the background (143). */}
      <CallPip active={inCall} theirShare={!!effectiveCanvasStream} matchOpen={matchOpen}
        them={theyAreIn ? remoteCameraStream : null} you={videoEnabled ? localStream ?? null : null}
        share={effectiveCanvasStream} otherFirst={otherFirst} shareLabel={shareLabel(otherFirst)} remoteVideo={stageVideo} />

      {openOffer && bookingId && (
        <OfferSheet
          bookingId={bookingId}
          recommendation={openOffer}
          pending={'pending' in openOffer && !!openOffer.pending}
          coachName={otherParticipantName}
          coachAvatarUrl={booking?.coachAvatarUrl}
          onPaid={() => setHeldOffer(openOffer.offer)}
          onClose={() => { setHeldOffer(null); setViewingOffer(null); }}
        />
      )}

    </div>
  );
}

/** A photo that fails to load is hidden, never a broken image over the stage; the round tile's grey shows. */
function hideBroken(e: React.SyntheticEvent<HTMLImageElement>) {
  e.currentTarget.style.visibility = 'hidden';
}

// ── A chip's close X ──────────────────────────────────────────────────────────

/** The X that closes a chip or the banner over the call: 32 drawn, 44 to tap. */
function ChipClose({ label, onClose }: { label: string; onClose: () => void }) {
  return (
    <button type="button" onClick={onClose} aria-label={label}
      className="ios-press relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-white/75 after:absolute after:-inset-1.5 after:content-[''] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
      <X size={14} weight="bold" aria-hidden="true" />
    </button>
  );
}

// ── Name labels ───────────────────────────────────────────────────────────────

/** A muted mic on a tile (141): the mic struck through on a small round badge of the call's glass. */
function MuteBadge({ label }: { label: string }) {
  return (
    <span className="call-glass flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full [text-shadow:none]">
      <MicrophoneSlash size={14} weight="fill" aria-label={label} />
    </span>
  );
}

/** One of three tiles: the feed object-fit contain at its own aspect, the name bottom left. */
function GridTile({ stream, name, muted = false, mirrored = false, micOff = false }: { stream: MediaStream | null; name: string; muted?: boolean; mirrored?: boolean; micOff?: boolean }) {
  const ref = useCallback((el: HTMLVideoElement | null) => { if (el) el.srcObject = stream; }, [stream]);
  return (
    <div className="relative min-h-0 min-w-0 overflow-hidden rounded-[12px] bg-call-tile" data-grid-tile={name}>
      {stream && (
        <video ref={ref} autoPlay playsInline muted={muted} className="h-full w-full object-contain"
          style={mirrored ? { transform: 'scaleX(-1)' } : undefined} />
      )}
      <NameLabel name={name} micOff={micOff} micOffLabel={name === 'You' ? 'Your microphone is off' : 'Microphone off'} className="absolute bottom-3 left-3" />
    </div>
  );
}

/**
 * A participant's name on their tile, plain white text in its bottom-left corner with a soft shadow to read
 * over any feed, no pill (141), Footnote 13/18; the self tile's says "You". Its mic, struck through on a
 * badge, while muted.
 */
function NameLabel({ name, micOff = false, micOffLabel = 'Microphone off', className = '' }: { name: string; micOff?: boolean; micOffLabel?: string; className?: string }) {
  return (
    <span
      className={`pointer-events-none inline-flex max-w-[calc(100%-16px)] items-center gap-1.5 text-[13px] leading-[18px] font-medium text-white select-none [text-shadow:0_1px_2px_rgba(0,0,0,0.6)] ${className}`}
      data-self-chip={name === 'You' ? '' : undefined}
    >
      {micOff && <MuteBadge label={micOffLabel} />}
      <span className="min-w-0 [overflow-wrap:anywhere]">{name}</span>
    </span>
  );
}

// ── Call control button ───────────────────────────────────────────────────────

/**
 * One round control in the bar, Meet's (141): 40 across on a larger screen with a 44 target, up to 56 on a
 * phone, white on the call tile's grey; `off` is a muted mic or a camera that is off, red as Meet draws it
 * (the one red, LC-3, and the only red in the call, 163); `active` an open panel (biscayne); `leave` is Leave, a wider white capsule; `plain` a
 * corner button with no fill, as Meet's panel buttons. `badge` a count on it.
 */
function CallButton({
  children,
  onPress,
  onPressAt,
  tone = 'fill',
  off = false,
  wide = false,
  corner = false,
  badge = 0,
  ariaLabel,
  className = 'flex',
}: {
  children: React.ReactNode;
  className?: string;
  onPress: () => void;
  /** Called with the button's box, for a menu anchored to it. */
  onPressAt?: (rect: DOMRect) => void;
  tone?: 'fill' | 'active' | 'leave' | 'plain';
  off?: boolean;
  wide?: boolean;
  /** In the bar's right corner: 44 across. */
  corner?: boolean;
  badge?: number;
  ariaLabel: string;
}) {
  // Red is only for a muted mic and a camera that is off (163): Leave is a white capsule.
  const fill = tone === 'leave' ? 'bg-white text-black'
    : tone === 'active' ? 'bg-biscayne text-white'
    : tone === 'plain' ? 'text-white hover:bg-white/[0.08]'
    : off ? 'bg-danger text-white' : 'bg-call-tile text-white';
  const size = corner ? 'aspect-square md:h-11 md:w-11'
    : wide ? 'aspect-[9/7] md:aspect-auto md:h-10 md:w-14' : 'aspect-square md:h-10 md:w-10';
  return (
    // Up to 56 round (72 for Leave) on a phone, where every button gives a share so the bar always fits.
    <div className={`${className} min-w-0 flex-col items-center gap-1 ${wide ? 'flex-[0_1_72px]' : 'flex-[0_1_56px]'} md:flex-none`}>
      <button
        type="button"
        aria-label={ariaLabel}
        title={ariaLabel}
        onClick={(e) => { onPress(); onPressAt?.(e.currentTarget.getBoundingClientRect()); }}
        // A 40 control takes a 44 target (GE-6) through the transparent ::after.
        className={`ios-press relative flex w-full items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 md:after:absolute md:after:-inset-0.5 md:after:content-[''] ${size} ${fill}`}
      >
        {children}
        {badge > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-biscayne px-1 text-[11px] leading-[13px] font-semibold text-white" aria-hidden="true">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </button>
    </div>
  );
}

// ── ICE server config ─────────────────────────────────────────────────────────

function buildCallConfig(): CallConfig {
  const turnUrl = import.meta.env.VITE_TURN_URL as string | undefined;
  const turnUsername = import.meta.env.VITE_TURN_USERNAME as string | undefined;
  const turnCredential = import.meta.env.VITE_TURN_CREDENTIAL as string | undefined;
  const turnsUrl = import.meta.env.VITE_TURNS_URL as string | undefined;

  const iceServers: RTCIceServer[] = [...DEFAULT_ICE_SERVERS];

  if (turnUrl && turnUsername && turnCredential) {
    iceServers.push({ urls: turnUrl, username: turnUsername, credential: turnCredential });
    iceServers.push({ urls: `${turnUrl}?transport=tcp`, username: turnUsername, credential: turnCredential });
  }

  if (turnsUrl && turnUsername && turnCredential) {
    iceServers.push({ urls: turnsUrl, username: turnUsername, credential: turnCredential });
  }

  return { iceServers };
}

const CALL_CONFIG = buildCallConfig();

// ── Public modal component ────────────────────────────────────────────────────

export function VideoCallModal({
  conversationId,
  bookingId,
  participantId,
  displayName,
  otherParticipantName,
  otherParticipantId,
  isCoach,
  booking,
  roomId: pairRoom,
  inviteToken,
  onClose,
}: VideoCallModalProps) {
  // Rule 9: a lesson signals on its pair's permanent room, as does a call
  // outside any lesson; a conversation's own call uses `conv-{conversationId}`.
  const roomId = callRoomId({ roomId: pairRoom, bookingId, booking, conversationId });

  const adapter = useMemo(() => new FirebaseSignalingAdapter(db), []);

  return (
    <WebRTCProvider
      signalingAdapter={adapter}
      participantId={participantId}
      callConfig={CALL_CONFIG}
    >
      <CallUI
        roomId={roomId}
        participantId={participantId}
        displayName={displayName}
        otherParticipantName={otherParticipantName}
        otherParticipantId={otherParticipantId}
        bookingId={bookingId}
        isCoach={isCoach}
        booking={booking}
        inviteToken={inviteToken}
        onClose={onClose}
      />
    </WebRTCProvider>
  );
}
